/* eslint-disable jsx-a11y/aria-activedescendant-has-tabindex */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { PlayIcon, CheckCircle2 } from 'lucide-react';

const Landing: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const [pricingMode, setPricingMode] = useState<'monthly' | 'yearly'>('yearly');

  const heroVideoContainerRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_isFullscreen, setIsFullscreen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(true);

  // Hero tab + playback state
  const [heroTab, setHeroTab] = useState<'polished' | 'raw'>('polished');
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const rawVideoRef = useRef<HTMLVideoElement>(null);
  const polishedVideoRef = useRef<HTMLVideoElement>(null);

  // Features State (Interactive Split Layout)
  const [activeFeature, setActiveFeature] = useState(0);
  const [activeUseCase, setActiveUseCase] = useState(0);
  const [isFeatureAutoPlaying, setIsFeatureAutoPlaying] = useState(false);
  const [featureProgress, setFeatureProgress] = useState(0);
  const featureIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [activeTestimonial, setActiveTestimonial] = useState(0);

  const useCases = [
    {
      tab: 'Customer Education',
      videoUrl: 'https://www.youtube.com/embed/--i8PJLUnzM?rel=0',
    },
    {
      tab: 'Product Marketing',
      videoUrl: 'https://www.youtube.com/embed/g5LnSWLabNI?rel=0',
    },
    {
      tab: 'Feature Release',
      videoUrl: 'https://www.youtube.com/embed/7ycT5a8dHAo?rel=0',
    },
  ];
  const features = [
    {
      title: 'Auto-zoom',
      description: 'Grab attention instantly. Keep viewers focused without manual editing.',
      src: 'https://pub-a88fc062d4ff4a159a4c3aca7f4e4435.r2.dev/Autozoom.mp4',
    },
    {
      title: 'Adjustable AI Transcriptions',
      description: 'Edit video like a text doc. Adjust AI-generated Texts with your desired words.',
      src: 'https://pub-a88fc062d4ff4a159a4c3aca7f4e4435.r2.dev/editAivoice.mp4',
    },
    {
      title: 'Transcription based trimming',
      description: 'Delete and trim video without touching the timeline.',
      src: 'https://pub-a88fc062d4ff4a159a4c3aca7f4e4435.r2.dev/transcription-trim.mp4',
    },
    {
      title: 'Background change',
      description: 'Stay strictly on brand. Maintain neat and clean professional layouts.',
      src: 'https://pub-a88fc062d4ff4a159a4c3aca7f4e4435.r2.dev/bg-change.mp4',
    },
    {
      title: 'Instant Export',
      description: 'Share & convert in seconds. Generate highly optimized links ready for emails or Slack—no rendering waits.',
      src: 'https://pub-a88fc062d4ff4a159a4c3aca7f4e4435.r2.dev/export.mp4',
    },
  ];

  // Auto-rotate testimonials
useEffect(() => {
  const timer = setInterval(() => {
    setActiveTestimonial((prev) => (prev + 1) % 3);
  }, 5000);
  return () => clearInterval(timer);
}, []);

  // Hero tab playback switcher
  useEffect(() => {
    if (!rawVideoRef.current || !polishedVideoRef.current) return;

    rawVideoRef.current.muted = isMuted;
    polishedVideoRef.current.muted = isMuted;

    if (isPlaying) {
      if (heroTab === 'polished') {
        polishedVideoRef.current.play().catch(() => {});
        rawVideoRef.current.pause();
      } else {
        rawVideoRef.current.play().catch(() => {});
        polishedVideoRef.current.pause();
      }
    } else {
      rawVideoRef.current.pause();
      polishedVideoRef.current.pause();
    }
  }, [heroTab, isMuted, isPlaying]);

  // Fullscreen Listener & Toggle Function
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Load Calendly widget script
  useEffect(() => {
    if (document.querySelector('script[src*="calendly.com/assets/external/widget.js"]')) {
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    script.onload = () => {
      if ((window as any).Calendly) {
        (window as any).Calendly.initInlineWidget({
          url: 'https://calendly.com/record-clicknrec/30min',
          parentElement: document.querySelector('.calendly-inline-widget'),
        });
      }
    };
    document.body.appendChild(script);
  }, []);

  const toggleFullscreen = () => {
    const video = heroTab === 'polished' ? polishedVideoRef.current : rawVideoRef.current;
    if (!video) return;

    // Mobile: use native video fullscreen
    if (window.innerWidth < 768) {
      if ((video as any).webkitEnterFullscreen) {
        (video as any).webkitEnterFullscreen();
      } else {
        video.requestFullscreen?.().catch(() => {});
      }
      return;
    }

    // Desktop: fullscreen the container
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen().catch((err) => {
        console.error(`Error attempting to enable fullscreen: ${err.message}`);
      });
    } else {
      document.exitFullscreen();
    }
  };
  useEffect(() => {
    if (isPlaying) setShowTooltip(false);
  }, [isPlaying]);

  // Auto-advance benefits feature list
  useEffect(() => {
    if (!isFeatureAutoPlaying) {
      if (featureIntervalRef.current) clearInterval(featureIntervalRef.current);
      return;
    }
    setFeatureProgress(0);
    const duration = 4000;
    const stepMs = 30;
    let elapsed = 0;
    featureIntervalRef.current = setInterval(() => {
      elapsed += stepMs;
      setFeatureProgress(Math.min((elapsed / duration) * 100, 100));
      if (elapsed >= duration) {
        setActiveFeature((prev) => (prev + 1) % features.length);
        elapsed = 0;
      }
    }, stepMs);
    return () => {
      if (featureIntervalRef.current) clearInterval(featureIntervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFeatureAutoPlaying, activeFeature]);

  // Intersection Observer for scroll animations
  const observeElement = useCallback((element: Element) => {
    if (!observerRef.current) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) entry.target.classList.add('revealed');
          });
        },
        { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
      );
    }
    observerRef.current.observe(element);
  }, []);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    const scrollElements = document.querySelectorAll(
      '.scroll-reveal, .scroll-reveal-left, .scroll-reveal-right, .scroll-reveal-from-bottom'
    );
    scrollElements.forEach(observeElement);
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, [observeElement]);

  const scrollToSection = (sectionId: string) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  useEffect(() => {
    rawVideoRef.current?.load();
    polishedVideoRef.current?.load();
  }, []);

  // Pre-fetch and cache both hero videos on mount
  useEffect(() => {
    if (rawVideoRef.current) rawVideoRef.current.load();
    if (polishedVideoRef.current) polishedVideoRef.current.load();
  }, []);

  return (
    <div className="min-h-screen bg-white text-slate-900 font-sans selection:bg-violet-100 selection:text-violet-900">

      {/* HEADER */}
      <header className={`fixed left-0 right-0 top-0 z-50 transition-all duration-200 ${isScrolled ? 'bg-white/90 backdrop-blur-md shadow-sm border-b border-slate-200' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <a href="/" className="flex items-center gap-2.5 group">
              <div className="w-9 h-9 bg-[#8656cd] rounded-[5px] flex items-center justify-center transition-transform group-hover:scale-105">
                <div className="w-[15px] h-[15px] bg-[#222222] rounded-full shadow-inner"></div>
              </div>
              <span className="text-xl font-semibold text-slate-900 tracking-tight">Clicknrec</span>
            </a>

            <nav className="hidden lg:flex items-center gap-8">
              {['Benefits', 'Use Cases', 'Pricing'].map((item) => (
                <button key={item} onClick={() => scrollToSection(item.toLowerCase().replace(' ', '-'))} className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">
                  {item}
                </button>
              ))}
            </nav>

            <div className="hidden md:flex items-center gap-4">
              <a href="https://app.clicknrec.com/login" className="text-sm font-medium text-slate-600 hover:text-slate-900">Log in</a>
              <button onClick={() => scrollToSection('book-demo')} className="rounded-lg px-4 py-2.5 text-sm font-semibold bg-violet-700 text-white hover:bg-violet-800 transition-all shadow-sm">
                Book a Demo
              </button>
            </div>

            <button type="button" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden p-2 text-slate-600">
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d={mobileMenuOpen ? 'M6 18L18 6M6 6l12 12' : 'M4 6h16M4 12h16M4 18h16'} />
              </svg>
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-slate-200 bg-white shadow-xl absolute w-full">
            <nav className="flex flex-col p-4 space-y-2">
              {['Features', 'Use Cases', 'Pricing'].map((item) => (
                <button key={item} onClick={() => scrollToSection(item.toLowerCase().replace(' ', '-'))} className="text-left px-4 py-3 text-base font-medium text-slate-700 hover:bg-slate-50 rounded-lg">
                  {item}
                </button>
              ))}
              <div className="pt-4 mt-2 border-t border-slate-100 flex flex-col gap-3">
                <a href="https://app.clicknrec.com/login" className="px-4 py-3 text-center text-base font-medium text-slate-700 border border-slate-200 rounded-lg">Log In</a>
                <a href="https://app.clicknrec.com/signUp" className="px-4 py-3 text-center text-base font-medium bg-violet-700 text-white rounded-lg">Get Started</a>
              </div>
            </nav>
          </div>
        )}
      </header>

      {/* HERO SECTION */}
      <section className="relative pt-32 pb-8 px-4 sm:px-6 lg:px-8 bg-white overflow-hidden">
        <div className="max-w-5xl mx-auto text-center flex flex-col items-center">

          <a href="https://chromewebstore.google.com/detail/clicknrec-screen-recorder/mplhaajghipagnidjkoemmajlgmcglmp" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-slate-200 shadow-sm hover:bg-slate-50 transition-colors mb-8">
            <div className="flex gap-0.5 text-amber-400">
              {[1, 2, 3, 4, 5].map((i) => (
                <svg key={i} className="w-4 h-4 fill-current" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>
              ))}
            </div>
            <span className="text-sm font-medium text-slate-700">5.0 on Chrome Web Store</span>
          </a>

          <h1 className="text-5xl sm:text-6xl md:text-7xl font-normal tracking-tight text-slate-900 mb-6 leading-[1.1]">
            Customer Education Made <br className="hidden sm:block" />
            <span className="text-violet-700 relative inline-block">
              Clear & Easy
              <svg className="absolute w-full h-3 -bottom-1 left-0 text-violet-200" viewBox="0 0 100 10" preserveAspectRatio="none"><path d="M0 5 Q 50 10 100 5" stroke="currentColor" strokeWidth="4" fill="transparent" /></svg>
            </span>
          </h1>

          <p className="text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto mb-10 leading-relaxed">
            Equip your Go-To-Market teams with Polished AI Product Demos that communicate value clearly for non-tech users and drive adoption.
          </p>

          <div className="flex flex-row items-center gap-3 mb-10 w-full sm:w-auto justify-center">
            <a href="https://app.clicknrec.com/signUp" className="rounded-lg px-5 py-2.5 text-sm font-semibold bg-violet-700 text-white hover:bg-violet-800 transition-all shadow-md text-center">
              Start for free
            </a>

            <button
              onClick={() => {
                setIsPlaying(true);
                setIsMuted(false);
                heroVideoContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }}
              className="rounded-lg px-5 py-2.5 text-sm font-semibold bg-white text-slate-900 border border-slate-200 hover:bg-slate-50 transition-all shadow-sm flex items-center justify-center gap-2"
            >
              <PlayIcon className="w-4 h-4" />
              Watch Demo
            </button>
          </div>
{/* Tab-based Video Player */}
<div ref={heroVideoContainerRef} className="w-full max-w-[1000px] mx-auto scroll-reveal-from-bottom relative z-10 mt-8 after:content-[''] after:block after:h-16 after:mx-8 after:rounded-b-3xl after:bg-gradient-to-b after:from-violet-200/30 after:to-transparent after:blur-xl after:-mt-4">
  <div ref={containerRef} className="relative rounded-2xl border border-slate-200 shadow-[0_0_40px_rgba(134,86,205,0.2),0_30px_60px_rgba(134,86,205,0.15)] group select-none">

    {/* Safari chrome */}
    <div className="bg-[#e8e8e8] rounded-t-xl">
              {/* Top bar: traffic lights + address bar */}
              <div className="flex items-center gap-2 px-2 py-1.5 border-b border-slate-300/50">
        <div className="flex gap-1.5 shrink-0">
          <div className="w-2.5 h-2.5 rounded-full bg-[#ff5f56]" />
          <div className="w-2.5 h-2.5 rounded-full bg-[#ffbd2e]" />
          <div className="w-2.5 h-2.5 rounded-full bg-[#27c93f]" />
        </div>
        <div className="flex items-center gap-2 text-slate-400 shrink-0">
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
          <svg className="w-3.5 h-3.5 opacity-40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
        </div>
        <div className="flex-1 flex justify-center overflow-hidden">
                  <div className="flex items-center px-2 py-1 bg-white rounded-md text-[10px] text-slate-500 w-full justify-center shadow-sm truncate">
                    {heroTab === 'polished' ? '💡 Clear education → Better Adoption' : '😑 Confused user → less engaged'}
                  </div>
                </div>
        <div className="w-[60px] shrink-0" />
      </div>
      <div className="flex items-end gap-0.5 px-2 pt-1.5">
        <button
          onClick={() => setHeroTab('polished')}
          className={`flex items-center gap-2 px-3 py-2 rounded-t-md text-xs font-medium transition-all ${heroTab === 'polished' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
        >
          <div className="w-3.5 h-3.5 rounded-[3px] bg-[#8656cd] flex items-center justify-center shrink-0">
  <div className="w-1.5 h-1.5 rounded-full bg-[#222222]" />
</div>
          Clicknrec 
        </button>
        <button
          onClick={() => setHeroTab('raw')}
          className={`flex items-center gap-2 px-3 py-2 rounded-t-md text-xs font-medium transition-all ${heroTab === 'raw' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
        >
        <div className="relative flex items-center justify-center w-3.5 h-3.5 shrink-0">
  <div className="absolute w-3 h-3 rounded-full bg-red-400 opacity-50 animate-ping" />
  <div className="w-2 h-2 rounded-full bg-red-500" />
</div>
          Raw Recording
        </button>
      </div>
    </div>

    {/* Video */}
    <div className="relative aspect-video bg-slate-900">
    <video ref={rawVideoRef} loop muted playsInline preload="auto"
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${heroTab === 'raw' ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        src="https://pub-a88fc062d4ff4a159a4c3aca7f4e4435.r2.dev/roughnew%20(3).mp4"
      />
      <video ref={polishedVideoRef} loop muted playsInline preload="auto"
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${heroTab === 'polished' ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        src="https://pub-a88fc062d4ff4a159a4c3aca7f4e4435.r2.dev/Captions%20(1).mp4"
      />
      {/* Control Bar */}
      <div className="absolute bottom-1 sm:bottom-4 left-1/2 -translate-x-1/2 z-50 flex items-center bg-[#404040]/90 backdrop-blur-md rounded-lg border border-white/10 px-1.5 sm:px-2.5 py-1 sm:py-1.5 text-white shadow-2xl transition-opacity opacity-100">
      <button onClick={() => setIsMuted(!isMuted)} className="p-1 sm:p-1.5 hover:text-white/80 transition-colors" aria-label={isMuted ? 'Unmute' : 'Mute'}>
          {isMuted ? (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M17.25 9.75 19.5 12m0 0 2.25 2.25M19.5 12l2.25-2.25M19.5 12l-2.25 2.25m-10.5-6 4.72-4.72a.75.75 0 0 1 1.28.53v15.88a.75.75 0 0 1-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.009 9.009 0 0 1 2.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75Z" /></svg>
          ) : (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M19.114 5.636a9 9 0 0 1 0 12.728M16.463 8.288a5.25 5.25 0 0 1 0 7.424M6.75 8.25l4.72-4.72a.75.75 0 0 1 1.28.53v15.88a.75.75 0 0 1-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.009 9.009 0 0 1 2.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75Z" /></svg>
          )}
        </button>
        <div className="w-[1px] h-5 bg-white/20 mx-2" />
        <div className="relative flex items-center justify-center">
          <button onClick={() => { setIsPlaying(!isPlaying); if (!isPlaying) setIsMuted(false); }} className="p-1.5 hover:text-white/80 transition-colors" aria-label={isPlaying ? 'Pause' : 'Play'}>
            {isPlaying ? (
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25v13.5m-7.5-13.5v13.5" /></svg>
            ) : (
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 0 1 0 1.972l-11.54 6.347a1.125 1.125 0 0 1-1.667-.986V5.653Z" /></svg>
            )}
          </button>
          {showTooltip && !isPlaying && (
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-4 pointer-events-none">
            <div className="relative px-3 sm:px-5 py-2 sm:py-3 bg-[#7b4ce5] text-white text-xs sm:text-base font-bold rounded-xl shadow-2xl shadow-violet-900/50 whitespace-nowrap animate-bounce ring-2 sm:ring-4 ring-violet-300/40"> 👆 Click to play
                <div className="absolute top-full left-1/2 -translate-x-1/2 border-[8px] border-transparent border-t-[#7b4ce5]"></div>
              </div>
            </div>
          )}
        </div>
        <div className="w-[1px] h-5 bg-white/20 mx-2" />
        <button onClick={toggleFullscreen} className="p-1.5 hover:text-white/80 transition-colors" aria-label="Fullscreen">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" /></svg>
        </button>
      </div>
    </div>

  </div>
</div>
        </div>
      </section>

      {/* FEATURES SECTION: Interactive Split Layout */}
      <section id="benefits" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <div className="mb-12 text-left max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 shadow-sm mb-5">
              <span className="w-2 h-2 rounded-full bg-violet-600" />
              <span className="text-sm font-medium text-slate-700">Benefits</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-normal text-slate-900 tracking-tight">
              Everything you need to <br className="hidden sm:block" />
              <span className="text-violet-700">create perfect demos.</span>
            </h2>
          </div>

          <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-center">
            {/* Left Side: Clickable Options List */}
            <div className="w-full lg:w-[40%] flex flex-col gap-1">
              {features.map((feature, index) => {
                const isActive = activeFeature === index;
                return (
                  <button
                    key={index}
                    onClick={() => { setActiveFeature(index); setIsFeatureAutoPlaying(false); }}
                    className={`relative text-left px-5 py-5 rounded-xl transition-all duration-300 w-full ${
                      isActive ? 'bg-white shadow-md border border-slate-100' : 'bg-transparent hover:bg-slate-50/80'
                    }`}
                  >
                    <div className="flex gap-4 items-center">
                      <div className={`flex-shrink-0 transition-colors duration-300 ${isActive ? 'text-violet-600' : 'text-slate-300'}`}>
                        <CheckCircle2 className="w-5 h-5" strokeWidth={isActive ? 2.5 : 2} />
                      </div>

                      <div className="flex flex-col flex-1">
                        <span className={`text-base font-semibold transition-colors duration-300 ${isActive ? 'text-slate-900' : 'text-slate-400'}`}>
                          {feature.title}
                        </span>

                        <div className={`grid transition-all duration-300 ease-in-out ${isActive ? 'grid-rows-[1fr] opacity-100 mt-1.5' : 'grid-rows-[0fr] opacity-0'}`}>
                          <div className="overflow-hidden">
                            <p className="text-slate-500 leading-relaxed text-sm pr-4 pb-0.5">
                              {feature.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Right Side: Massive Video Player */}
            <div className="w-full lg:w-[60%]">
              <div className="relative rounded-2xl overflow-hidden border border-slate-200 bg-slate-900 shadow-2xl shadow-slate-200/50 aspect-video group">
                {features.map((feature, index) => (
                  <video
                    key={index}
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="auto"
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${activeFeature === index ? 'opacity-100' : 'opacity-0'}`}
                    src={feature.src}
                  />
                ))}
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/20 to-transparent pointer-events-none" />

                <button
                  onClick={() => setIsFeatureAutoPlaying(!isFeatureAutoPlaying)}
                  className="absolute bottom-4 left-4 w-10 h-10 rounded-full bg-white/90 backdrop-blur-sm shadow-lg flex items-center justify-center z-10"
                  aria-label={isFeatureAutoPlaying ? 'Pause autoplay' : 'Resume autoplay'}
                >
                  <svg className="absolute inset-0 w-10 h-10 -rotate-90" viewBox="0 0 40 40">
                    <circle cx="20" cy="20" r="17" fill="none" stroke="#e2e8f0" strokeWidth="3" />
                    <circle
                      cx="20" cy="20" r="17" fill="none" stroke="#7c3aed" strokeWidth="3"
                      strokeDasharray={2 * Math.PI * 17}
                      strokeDashoffset={2 * Math.PI * 17 * (1 - featureProgress / 100)}
                      strokeLinecap="round"
                      style={{ transition: 'stroke-dashoffset 30ms linear' }}
                    />
                  </svg>
                  {isFeatureAutoPlaying ? (
                    <svg className="w-3.5 h-3.5 text-slate-900 relative z-10" fill="currentColor" viewBox="0 0 24 24">
                      <rect x="6" y="5" width="4" height="14" rx="1" />
                      <rect x="14" y="5" width="4" height="14" rx="1" />
                    </svg>
                  ) : (
                    <svg className="w-3.5 h-3.5 text-slate-900 relative z-10 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* USE CASES SECTION */}
      <section id="use-cases" className="relative bg-slate-950 pt-20 pb-32 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="max-w-6xl mx-auto">

          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 mb-8">
            <span className="w-2 h-2 rounded-full bg-violet-500" />
            <span className="text-sm font-medium text-slate-300">Use Cases</span>
          </div>
          <h2 className="text-4xl md:text-6xl font-normal text-white tracking-tight mb-12 max-w-4xl">
            Built for modern Go-To-Market teams
          </h2>

          {/* Tabs */}
          <div className="flex items-center gap-2 border-b border-white/10 mb-0">
            {useCases.map((uc, index) => (
              <button
                key={index}
                onClick={() => setActiveUseCase(index)}
                className={`px-5 py-4 text-sm md:text-base font-semibold transition-colors relative ${
                  activeUseCase === index ? 'text-white' : 'text-slate-500 hover:text-slate-300'
                }`}
              >
                {uc.tab}
                {activeUseCase === index && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-violet-500" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Floating card */}
        <div className="relative mt-0">
          <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-16">
          <div className="rounded-2xl overflow-hidden shadow-[0_0_60px_rgba(255,255,255,0.15)]">
          <div className="aspect-video bg-slate-900">
                <iframe
                  key={activeUseCase}
                  className="w-full h-full"
                  src={useCases[activeUseCase].videoUrl}
                  title={useCases[activeUseCase].tab}
                  frameBorder="0"
                  loading="lazy"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
          </div>
        </div>
      </section>

    {/* TESTIMONIALS SECTION */}
<section className="py-24 bg-slate-50 border-t border-slate-100 overflow-hidden">
  <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
    <div className="text-center mb-16">
      <p className="text-sm font-medium uppercase tracking-widest text-violet-600 mb-3">What teams say</p>
      <h2 className="text-3xl md:text-4xl font-normal text-slate-900 tracking-tight">Real results, real teams.</h2>
    </div>

    {(() => {
      const testimonials = [
        {
          quote: "Clicknrec has been a game-changer in our adoption numbers. Feature demos shipped along changelogs now have high retention from our non-technical realtors and gets activated instantly.",
          name: "Akshaya",
          role: "Product Manager · Leadrat Real Estate CRM",
          img: "https://pub-a88fc062d4ff4a159a4c3aca7f4e4435.r2.dev/Screenshot%202026-07-04%20at%207.24.40%E2%80%AFPM.png",
        },
        {
          quote: "I educate my users on support, sales, and marketing with Clicknrec. No worrying about pauses or restarts as the app handles it. Great export quality too.",
          name: "Om Sharma",
          role: "Solo Founder · Indie Hacker",
          img: "https://pub-a88fc062d4ff4a159a4c3aca7f4e4435.r2.dev/Screenshot%202026-07-04%20at%207.23.00%E2%80%AFPM.png",
        },
        {
          quote: "Non-tech users generate the maximum revenue — you can't let them churn. Clicknrec helped us retain them with education videos made in seconds.",
          name: "Albert",
          role: "Customer Success · Inventory Management App",
          img: "https://pub-a88fc062d4ff4a159a4c3aca7f4e4435.r2.dev/Screenshot%202026-07-04%20at%207.25.11%E2%80%AFPM.png",
        },
      ];

      const prev = (activeTestimonial + 2) % 3;
      const next = (activeTestimonial + 1) % 3;

      return (
        <>
          <div className="relative flex items-center justify-center min-h-[380px]">

            {/* Left arrow */}
            <button
              onClick={() => setActiveTestimonial(prev)}
              className="absolute left-0 z-30 w-10 h-10 rounded-full bg-white shadow-md border border-slate-200 flex items-center justify-center hover:bg-slate-50 transition-colors"
              aria-label="Previous"
            >
              <svg className="w-4 h-4 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
            </button>

            {/* Cards */}
            {testimonials.map((t, i) => {
              const isActive = i === activeTestimonial;
              const isPrev = i === prev;
              const isNext = i === next;
              return (
                <div
                  key={i}
                  className={`absolute transition-all duration-1000 ease-[cubic-bezier(0.22,1,0.36,1)] bg-white rounded-2xl border border-slate-200 p-8 text-center flex flex-col items-center gap-4 ${
                    isActive
                      ? 'z-20 scale-100 opacity-100 shadow-2xl max-w-xl'
                      : 'z-10 scale-90 opacity-30 shadow-md max-w-md pointer-events-none'
                  } ${isPrev && !isActive ? '-translate-x-[60%]' : ''} ${isNext && !isActive ? 'translate-x-[60%]' : ''}`}
                >
                  <p className={`text-slate-700 leading-relaxed ${isActive ? 'text-base md:text-lg' : 'text-sm'}`}>
                    "{t.quote}"
                  </p>
                  <img src={t.img} alt={t.name} className="w-12 h-12 rounded-full object-cover border-2 border-violet-200" />
                  <div className="flex gap-0.5 text-amber-400">
                    {[1,2,3,4,5].map((s) => (
                      <svg key={s} className="w-4 h-4 fill-current" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>
                    ))}
                  </div>
                  <div>
  <p className="text-sm font-semibold text-slate-900">{t.name}</p>
  <p className="text-xs font-medium text-violet-700 bg-violet-50 px-2.5 py-1 rounded-full mt-1 inline-block">{t.role}</p>
</div>
                </div>
              );
            })}

            {/* Right arrow */}
            <button
              onClick={() => setActiveTestimonial(next)}
              className="absolute right-0 z-30 w-10 h-10 rounded-full bg-white shadow-md border border-slate-200 flex items-center justify-center hover:bg-slate-50 transition-colors"
              aria-label="Next"
            >
              <svg className="w-4 h-4 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
            </button>
          </div>

          {/* Dots */}
          <div className="flex justify-center gap-2 mt-8">
            {testimonials.map((_, i) => (
              <button
                key={i}
                onClick={() => setActiveTestimonial(i)}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === activeTestimonial ? 'w-6 bg-slate-800' : 'w-1.5 bg-slate-300'
                }`}
                aria-label={`Testimonial ${i + 1}`}
              />
            ))}
          </div>
        </>
      );
    })()}
  </div>
</section>

      {/* PRICING SECTION */}
      <section id="pricing" className="relative py-24 bg-white border-t border-slate-200 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute top-10 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-slate-200 rounded-full blur-[100px] opacity-50 pointer-events-none" />
        <div className="max-w-6xl mx-auto relative">
          <div className="text-center mb-8 scroll-reveal">
            <h2 className="text-4xl md:text-5xl font-normal tracking-tight text-slate-900 mb-4">Simple pricing,<br /><span className="text-violet-700">scalable plans</span></h2>
            <p className="text-lg text-slate-500 max-w-2xl mx-auto">Designed for every team, from solo founders to GTM teams.</p>
          </div>
          <div className="flex flex-col items-center mb-12">
            <div className="flex p-1 bg-violet-100 rounded-xl mb-4">
              <button onClick={() => setPricingMode('monthly')} className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${pricingMode === 'monthly' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-900'}`}>Monthly</button>
              <button onClick={() => setPricingMode('yearly')} className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${pricingMode === 'yearly' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-900'}`}>Annually</button>
            </div>
            {pricingMode === 'yearly' && <span className="text-sm font-semibold text-violet-700 bg-violet-50 px-3 py-1 rounded-full">Save 17% with annual billing</span>}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch max-w-5xl mx-auto">
            {/* Free */}
            <div className="bg-white p-8 rounded-3xl border border-slate-200 flex flex-col hover:shadow-lg transition-shadow">
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Free</h3>
              <div className="text-4xl font-bold text-slate-900 mb-6">$0</div>
              <ul className="space-y-4 mb-8 flex-1">
                {['5 mins AI Video', '8 AI Voice Overs', '20 mins AI Polished Screen Recordings', 'Shareable Learning Pages'].map((f) => (
                  <li key={f} className="flex items-start gap-3 text-slate-600"><CheckCircle2 className="w-5 h-5 text-violet-600 shrink-0" /> {f}</li>
                ))}
              </ul>
              <a href="https://app.clicknrec.com/signUp" className="block w-full py-2.5 px-4 bg-slate-50 text-slate-900 text-sm font-semibold text-center rounded-lg border border-slate-200 hover:bg-slate-100">Try Now</a>
            </div>

            {/* Pro */}
            <div className="bg-white rounded-3xl border-2 border-violet-600 flex flex-col shadow-xl relative scale-[1.02] overflow-hidden">
              <div className="bg-slate-900 text-white text-center py-2 text-xs font-bold tracking-wide uppercase">Popular</div>
              <div className="p-8 flex flex-col flex-1">
                <h3 className="text-xl font-semibold text-slate-900 mb-2">Pro</h3>
                <div className="text-4xl font-bold text-slate-900 mb-1">{pricingMode === 'monthly' ? '$29' : '$24.92'} <span className="text-lg font-medium text-slate-500">/month</span></div>
                <div className="text-sm text-slate-500 mb-6">{pricingMode === 'yearly' ? 'Billed $299 annually' : 'Billed monthly'}</div>
                <ul className="space-y-4 mb-8 flex-1">
                  {['30 mins AI video/month', '20 AI Voice Overs','180 mins AI Polished Screen Recordings', 'Intros & Outros', 'Custom Branding', '2 Users', 'In-app Chat Support'].map((f) => (
                    <li key={f} className="flex items-start gap-3 text-slate-900 font-medium"><CheckCircle2 className="w-5 h-5 text-violet-600 shrink-0" /> {f}</li>
                  ))}
                </ul>
                <a href="https://app.clicknrec.com/signUp" className="block w-full py-2.5 px-4 bg-violet-700 text-white text-sm font-semibold text-center rounded-lg hover:bg-violet-800 shadow-md">Try Now</a>
              </div>
            </div>

            {/* Max */}
            <div className="bg-white p-8 rounded-3xl border border-slate-200 flex flex-col hover:shadow-lg transition-shadow">
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Max</h3>
              <div className="text-4xl font-bold text-slate-900 mb-1">{pricingMode === 'monthly' ? '$99' : '$82.50'} <span className="text-lg font-medium text-slate-500">/month</span></div>
              <div className="text-sm text-slate-500 mb-6">{pricingMode === 'yearly' ? 'Billed $990 annually' : 'Billed monthly'}</div>
              <ul className="space-y-4 mb-8 flex-1">
                {['Upto 100 mins AI video/month', '300 mins AI Polished Screen Recordings','Team workspace(5+ users)', 'Everything from Pro'].map((f) => (
                  <li key={f} className="flex items-start gap-3 text-slate-600"><CheckCircle2 className="w-5 h-5 text-violet-600 shrink-0" /> {f}</li>
                ))}
              </ul>
              <a href="https://app.clicknrec.com/signUp" className="block w-full py-2.5 px-4 bg-slate-50 text-slate-900 text-sm font-semibold text-center rounded-lg border border-slate-200 hover:bg-slate-100">Try Now</a>
            </div>
          </div>
        </div>
      </section>
{/* FINAL CTA */}
<section id="book-demo" className="py-28 bg-slate-900 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto flex flex-col lg:flex-row items-center gap-12">
          <div className="flex-1 text-center lg:text-left">
          <h2 className="scroll-reveal text-5xl md:text-6xl font-normal text-white tracking-tight leading-[1.08] mb-8">
              You're just minutes away<br />
              <span className="text-violet-400">from your first demo.</span>
            </h2>
            <p className="scroll-reveal text-xl text-slate-400 leading-relaxed">
              Schedule a quick walkthrough with Clicknrec's team and see how easy it is to ship polished, branded demos that drive adoption.
            </p>
          </div>
          <div className="scroll-reveal flex-1 w-full max-w-md bg-white rounded-2xl overflow-hidden shadow-2xl">
            <div
              className="calendly-inline-widget"
              data-url="https://calendly.com/record-clicknrec/30min"
              style={{ minWidth: '280px', height: '500px' }}
            />
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-12 bg-slate-50 border-t border-slate-200 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex flex-col items-start gap-1">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 bg-[#8656cd] rounded-[5px] flex items-center justify-center">
                <div className="w-[15px] h-[15px] bg-[#222222] rounded-full"></div>
              </div>
              <span className="text-xl font-semibold text-slate-900">Clicknrec</span>
            </div>
            <p className="text-xs text-slate-600 font-medium ml-12">Made on 🌍 with ❤️</p>
          </div>
          <div className="flex gap-6 text-sm font-medium text-slate-500">
            <a href="https://calendly.com/record-clicknrec/30min" className="hover:text-slate-900">Contact</a>
            <a href="/privacy" className="hover:text-slate-900">Privacy</a>
          </div>
          <div className="text-sm text-slate-400">© {new Date().getFullYear()} Clicknrec. All rights reserved.</div>
        </div>
      </footer>

    </div>
  );
};

export default Landing;